import type { ApplicationState, Job } from '../../redux/features/jobs/jobsSlice';

export type ApplicationStateSelectorDispatchPlan =
  | { kind: 'addApplied'; job: Job }
  | { kind: 'update'; jobId: string; applicationState: ApplicationState };

export type JobDataForApplicationState = {
  id?: string;
  objectID: string;
  normalisedTitle?: string;
  title?: string;
  companyId?: string;
  companyName?: string;
};

/**
 * Decides how to update Redux when the user picks an application pipeline state in
 * {@link ApplicationStateSelector}. Mirrors {@code useHitActions.handleSetApplicationState}:
 * if the job is not yet in `appliedJobs`, add it in one shot with `applicationState` and
 * `appliedAt`. Otherwise update the existing row. Avoids addAppliedJob without state after
 * a no-op update.
 */
export function resolveApplicationStateSelectorDispatch(input: {
  jobId: string;
  newState: ApplicationState;
  inApplied: boolean;
  jobData?: JobDataForApplicationState;
}): ApplicationStateSelectorDispatchPlan | null {
  const jobId = String(input.jobId);
  if (jobId === '') return null;

  if (!input.inApplied && input.newState !== null && input.jobData) {
    const id = String(input.jobData.id ?? input.jobData.objectID);
    if (!id) return null;
    const job: Job = {
      ...input.jobData,
      id,
      applicationState: input.newState,
      appliedAt: new Date().toISOString(),
    } as Job;
    return { kind: 'addApplied', job };
  }

  return {
    kind: 'update',
    jobId,
    applicationState: input.newState,
  };
}
